import { useState, useRef, useEffect } from 'react';
import useAnalyticsTracker from '../../../../../analytics/hooks/useAnalyticsTracker';
import { WorkspaceTrackingActions } from '../../tracking';
import {
  EntityList,
  ListConductorRawFilter,
  OperandValue,
  SortDirection,
  SortDropdown,
  matchFilters,
  useInMemoryFilteredList
} from '@hevoio/hevo-ui-toolkit';
import { IconBriefcase } from '@tabler/icons-react';
import { SVGIconComponent } from '@hevoio/hevo-ui-toolkit/app/icon/Icon';
import { WorkspacesAPI } from '../../api/WorkspacesAPI';
import { WORKSPACE_SORT_OPTIONS } from '../../constants/workspaceSortOptions';
import { IWorkspace } from '../../interfaces/workspace';
import { WorkspaceListTable } from './components/WorkspaceListTable/WorkspaceListTable';
import { CreateWorkspaceModal } from './components/CreateWorkspaceModal/CreateWorkspaceModal';
import { HdRbacButton } from '../../../../../access-control/components/rbac-button/HdRbacButton';
import { RbacPermissions } from '../../../../../core/interfaces/user';
import styles from './WorkspaceList.module.scss';

const SEARCH_FILTER_KEY = 'term';

const getFilterValue = (filterKey: string, item: IWorkspace): OperandValue => {
  if (filterKey === SEARCH_FILTER_KEY) {
    return [item.name, item.workspace_id];
  }
  if (filterKey === 'name') return item.name;
  if (filterKey === 'status') return item.status;
  return undefined;
};

export default function WorkspaceList() {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const { eventTrack } = useAnalyticsTracker();

  const {
    fetch,
    reset,
    updateMemoryData,
    data: listData
  } = useInMemoryFilteredList<IWorkspace>(
    () => WorkspacesAPI.getAllWorkspaces().then(res => res.data),
    (item, params) =>
      matchFilters(params.filters as ListConductorRawFilter[], filterKey =>
        getFilterValue(filterKey, item)
      ),
    undefined,
    undefined,
    (item, field) => {
      if (field === 'workspace_seq') return item.workspace_seq;
      if (field === 'name') return item.name.toLowerCase();
      if (field === 'created_ts') return item.created_ts;
      return item.name.toLowerCase();
    },
    true
  );

  // Keep a ref in sync so the delete callback always sees the latest list data,
  // avoiding a stale closure when the callback fires after a re-render.
  const listDataRef = useRef(listData);
  useEffect(() => { listDataRef.current = listData; }, [listData]);

  const handleWorkspaceDeleted = (workspaceId: string) => {
    updateMemoryData((listDataRef.current ?? []).filter(w => w.workspace_id !== workspaceId));
  };

  return (
    <div className={styles.page}>
      <CreateWorkspaceModal open={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} />
      <EntityList
        mode='standalone'
        entity='workspace'
        hidePagination
        compactControls
        showSearch
        searchKey={SEARCH_FILTER_KEY}
        searchPlaceholder='Search Workspaces'
        showFilters
        shimmerColumnsCount={5}
        dataSource$={fetch}
        onRefresh={reset}
        ListComponent={WorkspaceListTable}
        SortComponent={SortDropdown}
        alwaysShowToolbar
        searchAreaFullWidth
        StandaloneModeActions={() => (
          <HdRbacButton
            rbacPermission={RbacPermissions.TEAM_EDIT}
            dataId='create-workspace-btn'
            size='md'
            icon='plus'
            onClick={() => {
              eventTrack({ action: WorkspaceTrackingActions.CREATE_WORKSPACE_CLICK });
              setIsCreateModalOpen(true);
            }}
          >
            Create Workspace
          </HdRbacButton>
        )}
        sortComponentProps={{ sortOptions: WORKSPACE_SORT_OPTIONS }}
        newListConnectedTitle='No Workspaces Found'
        newListSVGIcon={IconBriefcase as unknown as SVGIconComponent}
        emptyListSVGIcon={IconBriefcase as unknown as SVGIconComponent}
        emptyTitle='No Matching Workspaces Found'
        emptySubTitle='Modify search term to find the desired workspaces'
        genericErrorTitle='Error while fetching Workspaces'
        genericErrorSubTitle='Retry Loading the Workspaces'
        listConductorOptions={{
          filterKeys: [SEARCH_FILTER_KEY, 'name', 'status'],
          sortKeys: ['name', 'created_ts']
        }}
        defaultListMeta={{
          filters: [],
          sort: { created_ts: SortDirection.DESC }
        }}
        removeSidePadding={true}
        listComponentProps={{ onDeleteSuccess: handleWorkspaceDeleted }}
      />
    </div>
  );
}
